<template>
    <div class="nc-audio" :class="isOperator ? 'nc-audio_operator' : 'nc-audio_customer'">
        <button
            type="button"
            class="nc-audio__btn"
            :disabled="!!error || loading"
            :aria-label="playing ? 'Пауза' : 'Воспроизвести'"
            @click="togglePlay"
        >
            <span v-if="loading" class="nc-audio__spinner" />
            <svg v-else-if="playing" width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                <rect x="2" y="1" width="4" height="12" rx="1"/>
                <rect x="8" y="1" width="4" height="12" rx="1"/>
            </svg>
            <svg v-else width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                <path d="M2 1.5L13 7 2 12.5V1.5z"/>
            </svg>
        </button>

        <div class="nc-audio__body">
            <!--
                [FUTURE: waveform]
                Заменить div.nc-audio__track на компонент <nc-audio-waveform>
                с пропсами :progress="progressPercent" :playing="playing"
            -->
            <div ref="track" class="nc-audio__track" @click="seekClick">
                <div class="nc-audio__fill" :style="{ width: progressPercent + '%' }" />
            </div>

            <div class="nc-audio__meta">
                <span class="nc-audio__time">{{ formatTime(currentTime) }}</span>
                <span v-if="duration" class="nc-audio__duration">/ {{ formatTime(duration) }}</span>
                <!--
                    [FUTURE: playback speed]
                    <button class="nc-audio__speed" @click="cycleSpeed">{{ playbackRate }}x</button>
                    data: playbackRate: 1
                    cycleSpeed() { this.playbackRate = [1, 1.5, 2][([1,1.5,2].indexOf(this.playbackRate)+1)%3]; this._audio.playbackRate = this.playbackRate }
                -->
                <span v-if="error" class="nc-audio__error-label">Недоступно</span>
            </div>
        </div>
    </div>
</template>

<script>
// Гарантирует, что одновременно играет только один плеер на странице.
// Module-level переменная — не Vuex, не EventBus, не $root.
let _activePlayer = null

export default {
    name: 'NewChatAudioPlayer',
    props: {
        src:             { type: String,  default: null },
        payload:         { type: String,  default: null },
        payloadEncoding: { type: String,  default: null },
        mimeType:        { type: String,  default: null },
        isOperator:      { type: Boolean, default: false },
    },
    data() {
        return {
            playing:     false,
            loading:     false,
            error:       false,
            currentTime: 0,
            duration:    0,
        }
    },
    computed: {
        progressPercent() {
            return this.duration ? (this.currentTime / this.duration) * 100 : 0
        },
    },
    created() {
        this._initAudio()
    },
    beforeDestroy() {
        this._destroyed = true
        this._destroyAudio()
    },
    methods: {
        async _initAudio() {
            if (!this.payload && !this.src) {
                this.error = true
                return
            }

            this.loading = true
            let blobUrl = null

            if (this.payload) {
                try {
                    // Normalize base64url → standard base64 if needed
                    let b64 = this.payload
                    if (this.payloadEncoding === 'base64url' || b64.includes('-') || b64.includes('_')) {
                        b64 = b64.replace(/-/g, '+').replace(/_/g, '/')
                        const pad = b64.length % 4
                        if (pad) b64 += '='.repeat(4 - pad)
                    }
                    const binary = atob(b64)
                    const bytes = new Uint8Array(binary.length)
                    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
                    const blob = new Blob([bytes], { type: this.mimeType || 'audio/ogg' })
                    blobUrl = URL.createObjectURL(blob)
                } catch { /* decode error — fall through to src fetch */ }
            }


            if (!blobUrl && this.src) {
                try {
                    const res = await fetch(this.src, { credentials: 'include' })
                    if (res.ok) {
                        const blob = await res.blob()
                        blobUrl = URL.createObjectURL(blob)
                    }
                } catch { /* network error */ }
            }

            if (!blobUrl) {
                if (!this._destroyed) {
                    this.loading = false
                    this.error = true
                }
                return
            }

            if (this._destroyed) {
                URL.revokeObjectURL(blobUrl)
                return
            }

            this._blobUrl = blobUrl
            const audio = new Audio(blobUrl)

            audio.addEventListener('canplay',        () => { this.loading = false })
            audio.addEventListener('durationchange', () => { this.duration = isFinite(audio.duration) ? audio.duration : 0 })
            audio.addEventListener('timeupdate',     () => { this.currentTime = audio.currentTime })
            audio.addEventListener('ended',          this._onEnded)
            audio.addEventListener('error',          this._onError)

            this._audio = audio
        },
        _destroyAudio() {
            if (this._audio) {
                this._audio.pause()
                this._audio.src = ''
                this._audio = null
            }
            if (this._blobUrl) {
                URL.revokeObjectURL(this._blobUrl)
                this._blobUrl = null
            }
            if (_activePlayer === this) _activePlayer = null
        },
        _onEnded() {
            this.playing = false
            this.currentTime = 0
            this._audio.currentTime = 0
            if (_activePlayer === this) _activePlayer = null
        },
        _onError() {
            this.loading = false
            this.error   = true
            this.playing = false
        },

        togglePlay() {
            if (!this._audio || this.error) return
            this.playing ? this._pause() : this._play()
        },
        _play() {
            if (_activePlayer && _activePlayer !== this) {
                _activePlayer._pause()
            }
            _activePlayer = this
            this._audio.play().catch(this._onError)
            this.playing = true
        },
        _pause() {
            this._audio.pause()
            this.playing = false
            if (_activePlayer === this) _activePlayer = null
        },

        seekClick(e) {
            if (!this._audio || !this.duration) return
            const rect  = this.$refs.track.getBoundingClientRect()
            const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
            this._audio.currentTime = ratio * this.duration
        },

        formatTime(sec) {
            if (!sec || !isFinite(sec)) return '0:00'
            const m = Math.floor(sec / 60)
            const s = Math.floor(sec % 60)
            return `${m}:${s.toString().padStart(2, '0')}`
        },
    },
}
</script>

<style lang="scss">
@import '../../../../../../gulp-src/app/scss/_variables';

.nc-audio {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 2px 0;
    min-width: 200px;
    max-width: 260px;

    &_operator {
        .nc-audio__btn  { background: rgba(255, 255, 255, 0.2); color: $global-white; }
        .nc-audio__track { background: rgba(255, 255, 255, 0.25); }
        .nc-audio__fill  { background: $global-white; }
        .nc-audio__time,
        .nc-audio__duration { color: rgba(255, 255, 255, 0.65); }
    }

    &_customer {
        .nc-audio__btn  { background: $global-dark-3a; color: $global-white; }
        .nc-audio__track { background: $global-light-gray; }
        .nc-audio__fill  { background: $global-dark-3a; }
        .nc-audio__time,
        .nc-audio__duration { color: $global-gray; }
    }

    &__btn {
        flex: 0 0 32px;
        width: 32px;
        height: 32px;
        border: none;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: opacity 0.15s ease;

        &:disabled {
            opacity: 0.45;
            cursor: default;
        }

        &:not(:disabled):hover {
            opacity: 0.8;
        }
    }

    &__body {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    &__track {
        position: relative;
        height: 3px;
        border-radius: 2px;
        cursor: pointer;

        &:hover .nc-audio__fill {
            opacity: 0.85;
        }
    }

    &__fill {
        position: absolute;
        inset: 0 auto 0 0;
        border-radius: 2px;
        transition: width 0.1s linear;
    }

    &__meta {
        display: flex;
        align-items: center;
        gap: 4px;
    }

    &__time,
    &__duration {
        font-size: 11px;
        line-height: 14px;
    }

    &__error-label {
        font-size: 11px;
        line-height: 14px;
        color: #e94a4a;
    }

    &__spinner {
        display: block;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        border: 2px solid transparent;
        border-top-color: currentColor;
        animation: nc-audio-spin 0.7s linear infinite;
    }
}

@keyframes nc-audio-spin {
    to { transform: rotate(360deg); }
}
</style>
